import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, ErrorHandler, provideZoneChangeDetection } from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter } from '@angular/router';
import { authHttpInterceptorFn, provideAuth0 } from '@auth0/auth0-angular';
import { MissingTranslationHandler, provideTranslateService, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ThemePreset } from '@plantefeve/common/constants/theme-preset';
import { withUrlInterceptor } from '@plantefeve/common/interceptors/base-url.interceptor';
import { headerInterceptor } from '@plantefeve/common/interceptors/header.interceptor';
import { httpErrorLoggingInterceptor } from '@plantefeve/common/interceptors/http-error-logging.interceptor';
import { CustomErrorHandlerService } from '@plantefeve/common/services/custom-error-handler.service';
import { DutchKeyMissingTranslationHandler } from '@plantefeve/common/services/dutch-key-missing-translation-handler';
import { providePrimeNgLocale } from '@plantefeve/common/shared/i18n/primeng-locale';
import { ToastService } from '@plantefeve/common/shared/services/toast.service';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { DialogService } from 'primeng/dynamicdialog';
import { environment } from '../environments/environment';
import { routes } from './app.routes';
import { DEFAULT_LANGUAGE } from './language/portal-language.service';
import { provideLanguage } from './language/provide-language';

const httpLoaderFactory = (http: HttpClient) => new TranslateHttpLoader(http, './assets/i18n/', '.json');

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    { provide: ErrorHandler, useClass: CustomErrorHandlerService },
    provideRouter(routes),
    provideTranslateService({
      defaultLanguage: DEFAULT_LANGUAGE,
      useDefaultLang: false,
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient],
      },
      missingTranslationHandler: { provide: MissingTranslationHandler, useClass: DutchKeyMissingTranslationHandler },
    }),
    provideAnimationsAsync(),
    provideAuth0({
      domain: environment.auth0.domain,
      clientId: environment.auth0.clientId,
      authorizationParams: {
        audience: environment.auth0.audience,
        redirect_uri: environment.auth0.redirectUri,
      },
      httpInterceptor: { allowedList: [`${environment.serverApiUrl}*`] },
    }),
    provideHttpClient(
      withInterceptors([
        withUrlInterceptor(environment.serverApiUrl),
        headerInterceptor,
        authHttpInterceptorFn,
        httpErrorLoggingInterceptor,
      ])
    ),
    providePrimeNG({
      theme: {
        preset: ThemePreset,
        options: {
          darkModeSelector: 'none',
        },
      },
    }),
    provideLanguage(),
    providePrimeNgLocale(),

    DialogService,
    MessageService,
    ToastService,
  ],
};
